import React from 'react';
import { View, TouchableOpacity } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

// React Native Reusables Components
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

// Utils
import {
  type PlanTier,
  getPlanDisplayName,
  getPlanFeatures,
} from '../../utils/planContext';
import { useTranslation } from '../../hooks/useTranslation';
import { brand } from '../../design-system/theme/tokens';

// Professional Design Tokens
const COLORS = {
  primary: brand.primary,
  secondary: '#F59E0B',
  success: '#10B981',
  background: '#FFFFFF',
  surface: '#F9FAFB',
  text: {
    primary: '#111827',
    secondary: '#4B5563',
    muted: '#9CA3AF',
  },
  border: '#E5E7EB',
};

interface PlanSummaryCardProps {
  planTier: PlanTier;
  price?: string | null;
  currency?: string;
  billingPeriod?: 'monthly' | 'yearly';
  onChangePlan?: () => void;
  showFeatures?: boolean;
  compact?: boolean;
  highlighted?: boolean;
}

/**
 * PlanSummaryCard - Reusable component for displaying plan details
 * 
 * Used in:
 * - Onboarding first slide (welcome with plan confirmation)
 * - Onboarding final slide (confirmation before payment)
 * - Plan selection screens
 */
export default function PlanSummaryCard({
  planTier,
  price,
  currency = 'USD',
  billingPeriod = 'monthly',
  onChangePlan,
  showFeatures = true,
  compact = false,
  highlighted = false,
}: PlanSummaryCardProps) {
  const { t } = useTranslation();
  const planName = getPlanDisplayName(planTier);
  const features = getPlanFeatures(planTier);

  const periodLabel = billingPeriod === 'yearly' 
    ? t('plans.perYear') || '/year'
    : t('plans.perMonth') || '/month';

  return (
    <Card
      className={`overflow-hidden ${highlighted ? 'border-2' : ''}`}
      style={highlighted ? { borderColor: COLORS.primary } : undefined}
    >
      <CardContent className={compact ? 'p-4' : 'p-6'}>
        {/* Header */}
        <View className="flex-row items-start justify-between mb-4">
          <View className="flex-1">
            <View className="flex-row items-center mb-1">
              <View
                className="w-10 h-10 rounded-xl items-center justify-center mr-3"
                style={{ backgroundColor: `${COLORS.primary}15` }}
              >
                <MaterialIcons 
                  name={planTier === 'tier3' ? 'star' : planTier === 'tier2' ? 'trending-up' : 'person'} 
                  size={24} 
                  color={COLORS.primary} 
                />
              </View>
              <View>
                <Text className="text-lg font-bold" style={{ color: COLORS.text.primary }}>
                  {planName}
                </Text>
                {highlighted && (
                  <Text className="text-xs font-medium" style={{ color: COLORS.primary }}>
                    {t('plans.yourSelectedPlan') || 'Your selected plan'}
                  </Text>
                )}
              </View>
            </View>
          </View>

          {/* Price */}
          {price && (
            <View className="items-end">
              <Text className="text-2xl font-bold" style={{ color: COLORS.text.primary }}>
                {price}
              </Text>
              <Text className="text-sm" style={{ color: COLORS.text.muted }}>
                {periodLabel}
              </Text>
            </View>
          )}
        </View>

        {/* Features List */}
        {showFeatures && features.length > 0 && (
          <View className={compact ? 'mb-3' : 'mb-4'}>
            {features.slice(0, compact ? 3 : features.length).map((feature, index) => (
              <View key={index} className="flex-row items-center mb-2">
                <MaterialIcons name="check-circle" size={18} color={COLORS.success} />
                <Text className="ml-2 text-sm flex-1" style={{ color: COLORS.text.secondary }}>
                  {feature}
                </Text>
              </View>
            ))}
            {compact && features.length > 3 && (
              <Text className="text-xs ml-6" style={{ color: COLORS.text.muted }}>
                +{features.length - 3} {t('plans.moreFeatures') || 'more features'}
              </Text>
            )}
          </View>
        )}

        {/* Change Plan Link */}
        {onChangePlan && (
          <TouchableOpacity
            onPress={onChangePlan}
            className="flex-row items-center justify-center py-2"
            accessibilityRole="button"
            accessibilityLabel={t('plans.changePlan') || 'Change plan'}
          >
            <MaterialIcons name="swap-horiz" size={18} color={COLORS.primary} />
            <Text className="ml-1 text-sm font-medium" style={{ color: COLORS.primary }}>
              {t('plans.changePlan') || 'Change plan'}
            </Text>
          </TouchableOpacity>
        )}
      </CardContent>
    </Card>
  );
}

